import React from 'react'
import { styled } from '@mui/material/styles'
import { Box, Typography, Paper, Divider, Grid, Chip } from '@mui/material'
import moment from 'moment'

const QuestionBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}))

const QuestionLabel = styled(Typography)(({ theme }) => ({
    fontFamily: 'Space Grotesk',
    fontWeight: 500,
    fontSize: '16px',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
}))

const AnswerText = styled(Typography)(() => ({
    whiteSpace: 'pre-line',
    fontFamily: 'Roboto',
    lineHeight: '1.6',
}))

const CategoryHeading = styled(Typography)(({ theme }) => ({
    fontFamily: 'Space Grotesk',
    fontWeight: 600,
    fontSize: '18px',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
}))

const HighlightedAnswer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(3),
}))

// Categorize questions into logical sections for better organization
const CATEGORIES = {
    EXPERIENCE: [
        'How many years of experience do you have?',
        'Have you worked in a remote position before?',
        'Have you worked with international clients before?',
    ],
    EDUCATION: [
        'Which university did you receive your last degree from?',
        'What is your highest level of education?',
        'When did you graduate?',
    ],
    TECHNICAL: [
        'What is your internet speed?',
        'What is the maximum memory on your laptop or PC?',
        'Equipment Test',
        'What is the operating system on your laptop or PC?',
        'Web Cam Test',
    ],
    AVAILABILITY: [
        'Earliest Start Date',
        'Are you comfortable working US hours?',
        'Do you have a USD receiving bank account?',
        'Desired Salary',
    ],
    SKILLS: [
        'Describe a time when you led',
        'How do you prioritize product features',
        'Share an instance where you',
    ],
    MISCELLANEOUS: [],
}

interface ApplicationDetailsTabProps {
    questionnaire: Record<string, any>
    jobTitle: string
    applyDate: number
}

export default function ApplicationDetailsTab({ questionnaire, jobTitle, applyDate }: ApplicationDetailsTabProps) {
    // Helper function to find the category for a question
    const findCategory = (question: string): string => {
        for (const [category, keywords] of Object.entries(CATEGORIES)) {
            if (keywords.some(keyword => question.includes(keyword))) {
                return category
            }
        }
        return 'MISCELLANEOUS'
    }

    // Group questions by category
    const categorizedQuestions: Record<string, Array<[string, any]>> = {
        EXPERIENCE: [],
        EDUCATION: [],
        TECHNICAL: [],
        AVAILABILITY: [],
        SKILLS: [],
        MISCELLANEOUS: [],
    }

    Object.entries(questionnaire).forEach(([question, answer]) => {
        const category = findCategory(question)
        categorizedQuestions[category].push([question, answer])
    })

    return (
        <Box>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" component="h2" sx={{ fontFamily: 'Space Grotesk', fontWeight: 600 }}>
                    Application for {jobTitle}
                </Typography>
                <Chip
                    label={`Applied: ${moment(applyDate).format('MMM D, YYYY')}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontFamily: 'Space Grotesk' }}
                />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Skills/Experience Section - Usually most important */}
            {categorizedQuestions.SKILLS.length > 0 && (
                <>
                    <CategoryHeading>Skills & Experience</CategoryHeading>
                    {categorizedQuestions.SKILLS.map(([question, answer], index) => (
                        <QuestionBox key={`skills-${index}`}>
                            <QuestionLabel>{question}</QuestionLabel>
                            <HighlightedAnswer elevation={0}>
                                <AnswerText>{String(answer)}</AnswerText>
                            </HighlightedAnswer>
                        </QuestionBox>
                    ))}
                </>
            )}

            {/* Experience Information */}
            {categorizedQuestions.EXPERIENCE.length > 0 && (
                <>
                    <CategoryHeading>Work Experience</CategoryHeading>
                    <Grid container spacing={3}>
                        {categorizedQuestions.EXPERIENCE.map(([question, answer], index) => (
                            <Grid item xs={12} md={6} key={`exp-${index}`}>
                                <QuestionBox>
                                    <QuestionLabel>{question}</QuestionLabel>
                                    <AnswerText>{String(answer)}</AnswerText>
                                </QuestionBox>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Education Information */}
            {categorizedQuestions.EDUCATION.length > 0 && (
                <>
                    <CategoryHeading>Education</CategoryHeading>
                    <Grid container spacing={3}>
                        {categorizedQuestions.EDUCATION.map(([question, answer], index) => (
                            <Grid item xs={12} md={6} key={`edu-${index}`}>
                                <QuestionBox>
                                    <QuestionLabel>{question}</QuestionLabel>
                                    <AnswerText>{String(answer)}</AnswerText>
                                </QuestionBox>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Availability Information */}
            {categorizedQuestions.AVAILABILITY.length > 0 && (
                <>
                    <CategoryHeading>Availability & Compensation</CategoryHeading>
                    <Grid container spacing={3}>
                        {categorizedQuestions.AVAILABILITY.map(([question, answer], index) => (
                            <Grid item xs={12} md={6} key={`avail-${index}`}>
                                <QuestionBox>
                                    <QuestionLabel>{question}</QuestionLabel>
                                    <AnswerText>
                                        {question.includes('Start Date')
                                            ? moment(String(answer)).format('MMMM D, YYYY')
                                            : String(answer)}
                                    </AnswerText>
                                </QuestionBox>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Technical Setup */}
            {categorizedQuestions.TECHNICAL.length > 0 && (
                <>
                    <CategoryHeading>Technical Setup</CategoryHeading>
                    <Grid container spacing={3}>
                        {categorizedQuestions.TECHNICAL.map(([question, answer], index) => (
                            <Grid item xs={12} md={6} key={`tech-${index}`}>
                                <QuestionBox>
                                    <QuestionLabel>{question}</QuestionLabel>
                                    <AnswerText>{String(answer)}</AnswerText>
                                </QuestionBox>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Miscellaneous Information */}
            {categorizedQuestions.MISCELLANEOUS.length > 0 && (
                <>
                    <CategoryHeading>Additional Information</CategoryHeading>
                    {categorizedQuestions.MISCELLANEOUS.map(([question, answer], index) => (
                        <QuestionBox key={`misc-${index}`}>
                            <QuestionLabel>{question}</QuestionLabel>
                            <AnswerText>{String(answer)}</AnswerText>
                        </QuestionBox>
                    ))}
                </>
            )}
        </Box>
    )
} 